import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useUser } from '../context/UserContext';
import { calcularVentaAnualRuts } from '../api/apiConfig';
import { formatNumberEsCL } from '../utils/numberFormat';

const normalizeHeader = (v) =>
  String(v || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[_\-.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const findCol = (headers, aliases) => {
  const set = new Set(aliases.map(normalizeHeader));
  return headers.find((h) => set.has(normalizeHeader(h))) || null;
};

const parseExcelRuts = async (file) => {
  const data = await file.arrayBuffer();
  const wb = XLSX.read(data, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return { ruts: [], columna: null, filas: 0 };

  const sheet = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  if (!rows.length) return { ruts: [], columna: null, filas: 0 };

  const headers = Object.keys(rows[0] || {});
  const colRut =
    findCol(headers, ['RUT', 'R.CLI', 'RUT_CLIENTE', 'RUT_CLI', 'ETIQUETAS DE FILA']) ||
    headers[0];

  const ruts = [];
  for (const row of rows) {
    const rutTxt = String(row[colRut] ?? '').trim();
    if (!rutTxt) continue;
    const upper = rutTxt.toUpperCase();
    if (upper === 'TOTAL' || upper === 'TOTALES') continue;
    ruts.push(rutTxt);
  }
  return { ruts, columna: colRut, filas: rows.length };
};

const VentaAnual = () => {
  const { user } = useUser();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [vista, setVista] = useState('resumen');
  const [q, setQ] = useState('');

  const filas = useMemo(() => {
    if (!result) return [];
    const base = vista === 'detalle' ? result.detalle || [] : result.resumen || [];
    const term = q.trim().toLowerCase();
    if (!term) return base;
    return base.filter((row) =>
      Object.values(row).some((v) => String(v ?? '').toLowerCase().includes(term)),
    );
  }, [result, vista, q]);

  const columnas = useMemo(() => {
    if (!filas.length) {
      return vista === 'detalle'
        ? ['rut', 'periodo', 'mes', 'ventas_netas_uf', 'ventas_netas']
        : [
            'rut_original',
            'rut',
            'VENTAS_ANUALES_UF',
            'VENTAS_ANUALES_PESOS',
            'MESES_DECLARADOS',
            'ULTIMO_MES_DECLARADO',
            'VENTA_HASTA',
          ];
    }
    return Object.keys(filas[0]);
  }, [filas, vista]);

  const procesar = async () => {
    if (!file) {
      setError('Selecciona un Excel con RUTs.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setResult(null);
      const parsed = await parseExcelRuts(file);
      if (!parsed.ruts.length) {
        throw new Error('No se encontraron RUTs válidos en el Excel.');
      }
      const resp = await calcularVentaAnualRuts(user, {
        op: 'procesar',
        nombre_archivo: file.name,
        ruts: parsed.ruts,
      });
      if (!resp?.ok) {
        throw new Error(resp?.error || 'El cálculo de venta anual falló.');
      }
      setResult(resp);
      setVista('resumen');
    } catch (e) {
      console.error(e);
      setError(e?.message || 'No se pudo calcular la venta anual.');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    if (!result?.resumen) return;
    const wb = XLSX.utils.book_new();
    const wsResumen = XLSX.utils.json_to_sheet(result.resumen);
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Ventas Anuales');
    if (result.detalle?.length) {
      const detalleFmt = result.detalle.map((d) => ({
        RUT: d.rut,
        Periodo: d.periodo,
        Mes: d.mes,
        'Venta Neta (Pesos)': d.ventas_netas,
        'Venta Neta (UF)': d.ventas_netas_uf,
      }));
      const wsDetalle = XLSX.utils.json_to_sheet(detalleFmt);
      XLSX.utils.book_append_sheet(wb, wsDetalle, 'Detalle Ventas');
    }
    const base = (result.meta?.nombre_archivo || 'venta_anual').replace(/\.xlsx?$/i, '');
    XLSX.writeFile(wb, `${base}_venta_anual.xlsx`);
  };

  const isNumericCol = (col) => {
    const s = String(col || '').toLowerCase();
    // Campos de texto con "mes"/"venta" en el nombre no son numéricos
    if (
      s === 'ultimo_mes_declarado' ||
      s === 'venta_hasta' ||
      s === 'rut_original' ||
      s.includes('fecha') ||
      s.includes('nombre')
    ) {
      return false;
    }
    return (
      s === 'rut' ||
      s === 'periodo' ||
      s === 'mes' ||
      s === 'meses_declarados' ||
      s.includes('ventas_anuales') ||
      s.includes('ventas_netas') ||
      s.includes('_uf') ||
      s.includes('_pesos') ||
      s.includes('monto')
    );
  };

  const preview = filas.slice(0, 300);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Venta anual</h1>
      <p className="text-gray-600 mb-6 max-w-3xl">
        Sube un Excel con un listado de RUTs para obtener las ventas de los últimos 12 meses
        (Formulario 29), hasta el cierre del mes calendario anterior.
      </p>

      <div className="seccion-datos mb-6 max-w-3xl p-6 md:p-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Carga del archivo</h2>

        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 mb-6">
          <li>
            Prepara un Excel (<code>.xlsx</code>) con una columna de <strong>RUT</strong>{' '}
            (<code>RUT</code>, <code>R.CLI</code>, <code>Etiquetas de fila</code>, etc.).
          </li>
          <li>
            El sistema consulta <code>PANEL.formulario_29</code> y, si hay varias declaraciones del
            mismo mes, usa la de mayor <code>IDTransaccion</code>.
          </li>
          <li>
            Suma la ventana fija de 12 meses. Luego puedes revisar el resumen / detalle y exportar.
          </li>
        </ol>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Archivo Excel</label>
            <input
              type="file"
              accept=".xlsx,.xls"
              className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-100 file:text-slate-800 hover:file:bg-slate-200"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            {file ? (
              <p className="mt-2 text-sm text-gray-600">Seleccionado: {file.name}</p>
            ) : (
              <p className="mt-2 text-sm text-gray-400">Ningún archivo seleccionado.</p>
            )}
          </div>

          <button
            type="button"
            className="btn-primary w-full py-3 text-base disabled:opacity-50"
            disabled={loading || !file}
            onClick={procesar}
          >
            {loading ? 'Calculando…' : 'Calcular venta anual'}
          </button>
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
        </div>
      </div>

      {result?.meta ? (
        <div className="seccion-datos mb-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Resultados</h2>
            <span className="text-sm text-gray-500">
              Ventana hasta: <strong>{result.meta.ventana_hasta}</strong>
            </span>
            <button type="button" className="btn-primary text-sm ml-auto" onClick={exportarExcel}>
              Exportar Excel
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              ['RUTs', result.meta.total_ruts],
              ['Con ventas', result.meta.ruts_con_ventas],
              ['Sin ventas', result.meta.ruts_sin_ventas],
              ['Total UF', result.meta.total_ventas_uf],
            ].map(([label, value]) => (
              <div key={label} className="rounded border border-gray-200 p-3 bg-white">
                <div className="text-xs text-gray-500">{label}</div>
                <div className="text-lg font-semibold">{formatNumberEsCL(value)}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mb-3 border-b border-gray-200 pb-2">
            {[
              ['resumen', 'Resumen'],
              ['detalle', 'Detalle mensual'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setVista(key)}
                className={`px-3 py-1 text-sm ${
                  vista === key
                    ? 'border-b-2 border-blue-600 text-blue-700 font-semibold'
                    : 'text-gray-500'
                }`}
              >
                {label}
              </button>
            ))}
            <input
              type="search"
              placeholder="Buscar…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="ml-auto border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div className="overflow-auto max-h-[520px] border border-gray-200 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {columnas.map((c) => (
                    <th
                      key={c}
                      className="px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap"
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, idx) => (
                  <tr key={idx} className="border-t border-gray-100">
                    {columnas.map((c) => {
                      const val = row[c];
                      const show =
                        val == null
                          ? '—'
                          : typeof val === 'number' || isNumericCol(c)
                            ? formatNumberEsCL(val)
                            : String(val);
                      return (
                        <td
                          key={c}
                          className={`px-3 py-1.5 whitespace-nowrap ${
                            typeof val === 'number' || isNumericCol(c) ? 'text-right' : ''
                          }`}
                        >
                          {show}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!preview.length ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-gray-400" colSpan={columnas.length}>
                      Sin datos
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {filas.length > preview.length ? (
            <p className="text-xs text-gray-500 mt-2">
              Mostrando {preview.length.toLocaleString('es-CL')} de{' '}
              {filas.length.toLocaleString('es-CL')} filas. Usa Exportar Excel para el total.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default VentaAnual;
